import React, { useState, useEffect, useCallback } from 'react';
import {
  PencilIcon,
  AlertCircle,
  RefreshCcw,
  PlusIcon,
  ClockIcon,
  XIcon,
  CheckIcon,
  Loader2,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Product, productService } from '@/services/productService';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logError } from '@/utils/errorHandler';

interface ProductDetailDescriptionProps {
  product: Product;
  onProductUpdate?: (updatedProduct: Product) => void;
}

export const ProductDetailDescription: React.FC<ProductDetailDescriptionProps> = ({
  product,
  onProductUpdate,
}) => {
  const [editing, setEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(product.description || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // i18n Step 5a: Get translations hook and current language
  const { getDescriptionLabels, getDescriptionNotification } = useProductDetailPageTranslations();
  const { i18n } = useTranslation();

  // Get current user and permissions from auth context
  const { user, checkPermission } = useAuth();

  // Permissions check
  const hasEditPermission = checkPermission ? checkPermission('product.change') : true;

  // Helper to strip HTML tags and get plain-text length
  const getPlainTextLength = useCallback((html: string): number => {
    if (!html) return 0;
    const text = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return text.length;
  }, []);

  const MAX_CHARS = 20000;
  const WARN_THRESHOLD = 18000;

  const [charCount, setCharCount] = useState(getPlainTextLength(product.description || ''));

  // Update local state when product prop changes
  useEffect(() => {
    setEditedDescription(product.description || '');
    setCharCount(getPlainTextLength(product.description || ''));
  }, [product.description, getPlainTextLength]);

  // Handle content change
  const handleContentChange = (newContent: string) => {
    setEditedDescription(newContent);
    setCharCount(getPlainTextLength(newContent));
  };

  // i18n Step 5b: Handle saving description with translated messages
  const handleSaveDescription = async () => {
    // Reset error state
    setSaveError(null);

    // Validate character limit
    if (charCount > MAX_CHARS) {
      toast.error(getDescriptionLabels.validation.maxLengthExceeded);
      return;
    }

    // Remove script tags for security
    const sanitizedDescription = editedDescription.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      ''
    );

    setSaving(true);
    try {
      // Make a real API call to update the product description
      if (product.id) {
        const updatedProduct = await productService.updateProduct(product.id, {
          description: sanitizedDescription,
        });

        // Update the product in the parent component if callback exists
        if (onProductUpdate) {
          onProductUpdate(updatedProduct);
        }

        setEditing(false);
        toast.success(getDescriptionNotification('updateSuccess'));
      } else {
        throw new Error(getDescriptionNotification('missingId'));
      }
    } catch (error: any) {
      logError(
        'Failed to update product description',
        {
          component: 'ProductDetailDescription',
          action: 'saveDescription',
          metadata: {
            productId: product.id,
            descriptionLength: sanitizedDescription.length,
            error: error,
          },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );

      // Set error message for retry UI with more details
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        getDescriptionLabels.errors.saveError;

      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Retry save after error
  const handleRetrySave = () => {
    setSaveError(null);
    handleSaveDescription();
  };

  // Handle cancel during editing
  const handleCancelEdit = () => {
    // Reset to original description
    setEditedDescription(product.description || '');
    setCharCount(getPlainTextLength(product.description || ''));
    setEditing(false);
    setSaveError(null);
  };

  // i18n Step 5c: Get the last edit info with translated fallbacks
  const getLastEditInfo = () => {
    const editorName =
      product.created_by || user?.name || getDescriptionLabels.states.unknownEditor;
    const editDate = new Date(product.updated_at || new Date());

    // Get localized relative time using browser's Intl API
    const getLocalizedTimeAgo = (date: Date): string => {
      try {
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();

        // Get current locale from i18next
        const locale = i18n.language || 'en';
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

        const diffInSeconds = Math.floor(diffInMs / 1000);
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInSeconds < 60) {
          return rtf.format(-diffInSeconds, 'second');
        } else if (diffInMinutes < 60) {
          return rtf.format(-diffInMinutes, 'minute');
        } else if (diffInHours < 24) {
          return rtf.format(-diffInHours, 'hour');
        } else if (diffInDays < 30) {
          return rtf.format(-diffInDays, 'day');
        } else {
          // For dates older than 30 days, show formatted date
          return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }).format(date);
        }
      } catch {
        // Fallback to simple format if localization fails
        return formatDistanceToNow(date, { addSuffix: true });
      }
    };

    return {
      name: editorName,
      date: editDate,
      timeAgo: getLocalizedTimeAgo(editDate),
    };
  };

  const lastEdit = getLastEditInfo();

  // i18n Step 5d: Action button for the section header with translated aria-label
  const actionButton = !editing && hasEditPermission && product.description && (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setEditing(true)}
      className="h-8 px-2"
      aria-label={getDescriptionLabels.edit.buttonAriaLabel}
    >
      <PencilIcon className="mr-2 size-4" />
      {getDescriptionLabels.edit.button}
    </Button>
  );

  // i18n Step 5e: Section description with last edit info using translations
  const sectionDescription = product.description ? (
    <div className="mt-1 flex items-center text-sm text-muted-foreground">
      <ClockIcon className="mr-1 size-3" />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              {getDescriptionLabels.states.lastEditedBy
                .replace('{{name}}', lastEdit.name)
                .replace('{{timeAgo}}', lastEdit.timeAgo)}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{format(lastEdit.date, 'PPpp')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  ) : undefined;

  return (
    <CollapsibleSection
      title={getDescriptionLabels.title}
      description={sectionDescription}
      actions={actionButton}
      defaultOpen={true}
      id="product-description-section"
    >
      <AnimatePresence mode="wait">
        {saveError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="size-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{saveError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetrySave}
                  className="ml-2"
                  aria-label={getDescriptionNotification('retryAriaLabel')}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="mr-1 size-3 animate-spin" />
                  ) : (
                    <RefreshCcw className="mr-1 size-3" />
                  )}
                  {getDescriptionNotification('retry')}
                </Button>
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {editing ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {/* i18n Step 5f: RichTextEditor with translated attributes */}
            <RichTextEditor
              value={editedDescription}
              onChange={handleContentChange}
              placeholder={getDescriptionLabels.edit.placeholder}
              aria-label={getDescriptionLabels.edit.ariaLabel}
              aria-required={getDescriptionLabels.edit.ariaRequired}
              aria-invalid={charCount > MAX_CHARS}
            />
            <div className="flex items-center justify-between">
              {/* i18n Step 5g: Character count with translated text and interpolation */}
              <span
                className={`text-xs ${charCount > MAX_CHARS ? 'font-medium text-red-500' : charCount > WARN_THRESHOLD ? 'text-amber-500' : 'text-slate-500'}`}
                aria-live="polite"
              >
                {getDescriptionLabels.edit.characterCount.replace(
                  '{{count}}',
                  charCount.toString()
                )}
                {charCount > MAX_CHARS && (
                  <span className="ml-2 text-red-500">
                    {getDescriptionLabels.edit.characterOverLimit.replace(
                      '{{over}}',
                      (charCount - MAX_CHARS).toString()
                    )}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                {/* i18n Step 5h: Action buttons with translated text and aria-labels */}
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  aria-label={getDescriptionLabels.edit.cancelAriaLabel}
                >
                  <XIcon className="mr-2 size-4" />
                  {getDescriptionLabels.edit.cancel}
                </Button>
                <Button
                  onClick={handleSaveDescription}
                  disabled={saving || charCount > MAX_CHARS}
                  aria-label={getDescriptionLabels.edit.saveAriaLabel}
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      {getDescriptionLabels.edit.saving}
                    </>
                  ) : (
                    <>
                      <CheckIcon className="mr-2 size-4" />
                      {getDescriptionLabels.edit.save}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="prose max-w-none dark:prose-invert">
            {product.description ? (
              <div
                dangerouslySetInnerHTML={{ __html: product.description }}
                className="rich-text-content"
              />
            ) : (
              /* i18n Step 5i: Empty state with translated content */
              <div className="flex flex-col items-center justify-center rounded-md border border-dashed bg-muted/20 p-12 text-center">
                <div className="rounded-full bg-primary/10 p-3">
                  <PlusIcon className="size-6 text-primary" />
                </div>
                <h3 className="mt-4 text-lg font-medium">{getDescriptionLabels.empty.title}</h3>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  {getDescriptionLabels.empty.subtitle}
                </p>

                {hasEditPermission && (
                  <Button
                    onClick={() => setEditing(true)}
                    className="mt-4"
                    aria-label={getDescriptionLabels.empty.addButtonAriaLabel}
                  >
                    <PencilIcon className="mr-2 size-4" />
                    {getDescriptionLabels.empty.addButton}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </AnimatePresence>
    </CollapsibleSection>
  );
};

export default ProductDetailDescription;
